import React, { useState } from 'react';
import { RotateCcw, Clock, CheckCircle2, ArrowRight, XCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { useMyServiceRequests, useCloneServiceRequest } from './serviceRequestHooks';
import ServiceRequestDetailModal from './ServiceRequestDetailModal';

const STATUS_PILL = {
  submitted:   { icon: Clock,        text: 'text-blue-500',   bg: 'bg-blue-100 dark:bg-blue-900/30',   label: 'Submitted' },
  in_progress: { icon: ArrowRight,   text: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', label: 'In progress' },
  completed:   { icon: CheckCircle2, text: 'text-green-500',  bg: 'bg-green-100 dark:bg-green-900/30', label: 'Completed' },
  cancelled:   { icon: XCircle,      text: 'text-red-500',    bg: 'bg-red-100 dark:bg-red-900/30',     label: 'Cancelled' },
};

export default function ServiceRequestHistory({ onRequestAgain }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data, isLoading } = useMyServiceRequests();
  const clone = useCloneServiceRequest();
  const [detailId, setDetailId] = useState(null);

  const rows = data?.service_requests || [];

  const handleClone = async (publicId) => {
    try {
      const result = await clone.mutateAsync(publicId);
      onRequestAgain?.(result?.draft);
    } catch (e) {
      console.error(e);
      toast.error('Could not start a re-request.');
    }
  };

  if (isLoading) {
    return <div className={`text-sm font-rasa ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading your requests…</div>;
  }

  if (rows.length === 0) {
    return (
      <div className={`rounded-lg border-2 border-dashed p-10 text-center ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="text-sm font-rasa text-gray-500">You haven't requested any services yet.</div>
      </div>
    );
  }

  return (
    <>
      <div className={`rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className={`text-left ${isDark ? 'bg-gray-900/40 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
            <tr>
              <th className="px-4 py-2 font-rasa font-medium">Service</th>
              <th className="px-4 py-2 font-rasa font-medium">Status</th>
              <th className="px-4 py-2 font-rasa font-medium">Submitted</th>
              <th className="px-4 py-2 font-rasa font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const status = STATUS_PILL[r.status] || STATUS_PILL.submitted;
              const Icon = status.icon;
              const primary = r.triage_state === 'pending'
                ? "Unsure — waiting on triage"
                : (r.items?.[0]?.label || 'Service request');
              const extra = (r.items?.length || 0) > 1 ? ` + ${r.items.length - 1} more` : '';
              return (
                <tr
                  key={r.public_id}
                  className={`${isDark ? 'border-t border-gray-700 hover:bg-gray-700/40' : 'border-t border-gray-100 hover:bg-gray-50'} cursor-pointer`}
                  onClick={() => setDetailId(r.public_id)}
                >
                  <td className="px-4 py-3 font-rasa">
                    <div className="flex items-center gap-2">
                      {r.triage_state === 'pending' && <AlertCircle size={14} className="text-orange-500" />}
                      <span>{primary}{extra}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-rasa ${status.bg} ${status.text}`}>
                      <Icon size={12} /> {status.label}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-rasa text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClone(r.public_id); }}
                      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md font-rasa text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                    >
                      <RotateCcw size={12} /> Request again
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailId && (
        <ServiceRequestDetailModal
          publicId={detailId}
          onClose={() => setDetailId(null)}
          onRequestAgain={(draft) => { setDetailId(null); onRequestAgain?.(draft); }}
        />
      )}
    </>
  );
}
